import React, {useCallback} from 'react';
import {Button, Checkbox, FormControlLabel, TextField} from '@mui/material';
import {COLORS, SIZES, Typography} from '@fupa/fupa-uikit';
import {CreateResultResponse} from 'app/components/match/liveResult/types';
import styled from 'styled-components';
import {Action, DataEntry, Label} from 'app/components/match/liveResult/CommonRowStyles';
import {LoadingButton} from '@mui/lab';
import {Controller, SubmitHandler, useForm, UseFormSetError} from 'react-hook-form';
import {isNumber} from 'app/helpers/ValidationRules';
import {useStoreSofttickerFormDataInSessionStorage} from 'app/hooks/useStoreSofttickerFormDataInSessionStorage';

const FormContainer = styled.form`
  display: grid;
  grid-template-columns: repeat(2, 1fr) repeat(2, ${SIZES['42']}) repeat(2, 1fr);
  gap: ${SIZES['16']};
  margin: auto;
`;

const GridElement = styled.div<{shrink?: boolean}>`
  grid-column: span 2;
  display: grid;
  align-content: center;
  height: ${SIZES['42']};
  ${props =>
    props.shrink &&
    `
    @media (max-width: 400px) {
      grid-column: span 1;
    }
  `}
`;

const ErrorContainer = styled.div`
  display: grid;
  grid-column: span 6;
  height: ${SIZES['48']};
  overflow-y: scroll;
`;

const ErrorTypography = styled(Typography)`
  &&& {
    color: ${COLORS.red};
  }
`;

const CustomNumberTextField = styled(TextField)({
  '& .MuiInputBase-input': {
    paddingLeft: '8px',
    paddingRight: '8px',
    textAlign: 'center',
    '&::-webkit-outer-spin-button, &::-webkit-inner-spin-button': {
      '-webkit-appearance': 'none',
      margin: 0,
    },
    '&[type=number]': {
      '-moz-appearance': 'textfield',
    },
  },
  '& .MuiOutlinedInput-root': {
    maxWidth: 42,
    '&:hover fieldset': {
      borderColor: COLORS.fupaSecondary,
    },
    '&.Mui-focused fieldset': {
      borderColor: COLORS.fupaSecondary,
    },
  },
});

interface UpdateResultFormProps {
  match: any;
  data: CreateResultResponse | null;
  onClose: () => void;
  onSubmit: (validatedFormData: ResultFormInputs, setFieldError: UseFormSetError<ResultFormInputs>) => void;
  submissionError: string | null;
  loading: boolean;
}

export type ResultFormInputs = {
  homeGoals: number;
  awayGoals: number;
  minute: number;
  final: boolean;
};

export default function UpdateResultForm({
  match,
  data,
  onClose,
  onSubmit,
  submissionError,
  loading,
}: UpdateResultFormProps) {
  const {
    register,
    handleSubmit,
    formState: {errors},
    setError,
    getValues,
    setValue,
    control,
  } = useForm<ResultFormInputs>({
    defaultValues: {
      minute: data?.currentMatchMinute,
      final: false,
    },
    mode: 'onTouched',
    reValidateMode: 'onChange',
  });

  const initializeResultForm = useCallback(
    (data: ResultFormInputs) => {
      Object.keys(data).forEach(key => {
        setValue(key as keyof ResultFormInputs, data[key]);
      });
    },
    [setValue]
  );

  useStoreSofttickerFormDataInSessionStorage<ResultFormInputs>('liveResultFormData', getValues, initializeResultForm);

  const onValidationSuccess: SubmitHandler<ResultFormInputs> = async data => {
    const submitData = {...data, final: data.final ?? false};
    onSubmit(submitData, setError);
  };

  return (
    <FormContainer>
      <GridElement>
        <Label variant='body1'>Spielstand</Label>
      </GridElement>
      <GridElement>
        <DataEntry>
          <CustomNumberTextField
            error={!!errors.homeGoals}
            type='number'
            variant='outlined'
            size='small'
            inputProps={{inputMode: 'numeric'}}
            {...register('homeGoals', {
              required: 'Gib die Anzahl der Heimtore an.',
              validate: {isNumber},
              max: {value: 99, message: 'Die Anzahl der Heimtore darf maximal 99 sein.'},
              min: {value: 0, message: 'Die Anzahl der Heimtore muss mindestens 0 sein.'},
            })}
          />
          <Label variant='body1'>:</Label>
          <CustomNumberTextField
            error={!!errors.awayGoals}
            type='number'
            variant='outlined'
            size='small'
            inputProps={{inputMode: 'numeric'}}
            {...register('awayGoals', {
              required: 'Gib die Anzahl der Gasttore an.',
              validate: {isNumber},
              max: {value: 99, message: 'Die Anzahl der Gasttore darf maximal 99 sein.'},
              min: {value: 0, message: 'Die Anzahl der Gasttore muss mindestens 0 sein.'},
            })}
          />
        </DataEntry>
      </GridElement>
      <GridElement />
      <GridElement>
        <Label variant='body1'>Spielminute</Label>
      </GridElement>
      <GridElement shrink={true}>
        <DataEntry>
          <CustomNumberTextField
            error={!!errors.minute}
            type='number'
            variant='outlined'
            size='small'
            inputProps={{inputMode: 'numeric'}}
            {...register('minute', {
              required: 'Gib die aktuelle Spielminute an.',
              validate: {isNumber},
              max: {value: 199, message: 'Es darf maximal Minute 199 angegeben werden.'},
              min:
                match?.live?.minute && !data?.canStoreFinalResult
                  ? {value: match.live.minute, message: 'Es wurde bereits ein späterer Zwischenstand gemeldet.'}
                  : {value: 0, message: 'Es muss mindestens Minute 0 angegeben sein.'},
            })}
          />
        </DataEntry>
      </GridElement>
      <GridElement>
        <FormControlLabel
          disabled={!data?.canStoreFinalResult}
          control={
            <Controller
              control={control}
              name={'final'}
              render={({field: {value, ref, ...field}}) => <Checkbox inputRef={ref} checked={value} {...field} />}
            />
          }
          label='Endstand?'
          labelPlacement='start'
        />
      </GridElement>
      <ErrorContainer>
        {errors.homeGoals?.message && <ErrorTypography variant='caption1'>{errors.homeGoals.message}</ErrorTypography>}
        {errors.awayGoals?.message && <ErrorTypography variant='caption1'>{errors.awayGoals.message}</ErrorTypography>}
        {errors.minute?.message && <ErrorTypography variant='caption1'>{errors.minute.message}</ErrorTypography>}
        {submissionError && <ErrorTypography variant='caption1'>{submissionError}</ErrorTypography>}
      </ErrorContainer>
      <Action span={3}>
        <Button variant='text' sx={{color: COLORS.darkestGrey}} onClick={onClose}>
          Abbrechen
        </Button>
      </Action>
      <Action span={3} justify='end'>
        <LoadingButton
          variant='contained'
          color='secondary'
          onClick={handleSubmit(onValidationSuccess)}
          loading={loading}>
          Speichern
        </LoadingButton>
      </Action>
    </FormContainer>
  );
}
